import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Search,
  Copy,
  Edit,
  Trash2,
  MoreHorizontal,
  Star,
  StarOff,
  Check,
  Tag,
  Sparkles,
  Files,
  Eye,
} from "lucide-react";
import { Button } from "@foundation/components/ui/button";
import { Input } from "@foundation/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@foundation/components/ui/card";
import { Badge } from "@foundation/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@foundation/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@foundation/components/ui/dropdown-menu";
import { Label } from "@foundation/components/ui/label";
import { ScrollArea } from "@foundation/components/ui/scroll-area";
import { Textarea } from "@foundation/components/ui/textarea";
import { supabase } from "@foundation/utils/supabaseClient";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@foundation/components/ui/select";
import { useUser } from "@foundation/contextapi/UserContext";
import { useToast } from "@foundation/hooks/use-toast";
import ScopingAILayout from "../components/RevisrLayout";
import {
  fetchPrompts,
  savePrompt,
  updatePrompt,
  deletePrompt,
  createCustomVersionOfDefaultPrompt,
  PromptTemplate,
  PromptFormData,
} from "../services/promptService";

export default function KnowledgeBasePrompts() {
  const { user } = useUser();
  const { toast } = useToast();
  const [prompts, setPrompts] = useState<PromptTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<PromptTemplate | null>(
    null
  );
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [viewingPrompt, setViewingPrompt] = useState<PromptTemplate | null>(
    null
  );
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [formData, setFormData] = useState<PromptFormData>({
    name: "",
    description: "",
    content: "",
  });

  useEffect(() => {
    if (user) {
      loadPrompts();
    }
  }, [user]);

  const loadPrompts = async () => {
    try {
      setIsLoading(true);
      const data = await fetchPrompts();
      setPrompts(data);
    } catch (error) {
      console.error("Error fetching prompts:", error);
      toast({
        title: "Error",
        description: "Failed to load prompt templates",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePrompt = async () => {
    try {
      if (!formData.name.trim() || !formData.content.trim()) {
        toast({
          title: "Missing fields",
          description: "Prompt name and content are required",
          variant: "destructive",
        });
        return;
      }

      if (editingPrompt) {
        // Update existing prompt
        const updatedPrompt = await updatePrompt(editingPrompt.id, formData);
        setPrompts((prev) =>
          prev.map((prompt) =>
            prompt.id === editingPrompt.id ? updatedPrompt : prompt
          )
        );
        toast({
          title: "Success",
          description: "Prompt template updated successfully",
        });
      } else {
        // Create new prompt
        const newPrompt = await savePrompt(formData);
        setPrompts((prev) => [newPrompt, ...prev]);
        toast({
          title: "Success",
          description: "Prompt template added successfully",
        });
      }

      // Reset form and close dialog
      setIsDialogOpen(false);
      setEditingPrompt(null);
      setFormData({
        name: "",
        description: "",
        content: "",
      });
    } catch (error) {
      console.error("Error saving prompt:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to save prompt",
        variant: "destructive",
      });
    }
  };

  const handleEditPrompt = (prompt: PromptTemplate) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      description: prompt.description,
      content: prompt.content,
    });
    setIsDialogOpen(true);
  };

  const handleDeletePrompt = async (id: string) => {
    try {
      if (!confirm("Are you sure you want to delete this prompt template?"))
        return;

      await deletePrompt(id);
      setPrompts((prev) => prev.filter((prompt) => prompt.id !== id));

      toast({
        title: "Success",
        description: "Prompt template deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting prompt:", error);
      toast({
        title: "Error",
        description: "Failed to delete prompt template",
        variant: "destructive",
      });
    }
  };

  const handleCopyPrompt = async (prompt: PromptTemplate) => {
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopiedId(prompt.id);

      toast({
        title: "Copied",
        description: "Prompt content copied to clipboard",
      });

      // Reset copy indicator after 2 seconds
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleDuplicatePrompt = async (prompt: PromptTemplate) => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }

    try {
      if (prompt.user_id !== "system") {
        toast({
          title: "Cannot Duplicate",
          description: "Only default prompts can be duplicated",
          variant: "destructive",
        });
        return;
      }

      // Create custom version with "(Copy)" suffix
      await createCustomVersionOfDefaultPrompt({
        name: `${prompt.name} (Copy)`,
        description: prompt.description,
        content: prompt.content,
      });

      // Refresh prompts
      await loadPrompts();

      toast({
        title: "Success",
        description: "Prompt duplicated successfully",
      });
    } catch (error) {
      console.error("Error duplicating prompt:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to duplicate prompt",
        variant: "destructive",
      });
    }
  };

  const handleViewPrompt = (prompt: PromptTemplate) => {
    setViewingPrompt(prompt);
    setIsViewDialogOpen(true);
  };

  const filteredPrompts = prompts.filter((prompt) => {
    const matchesSearch =
      prompt.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prompt.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prompt.content?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <ScopingAILayout>
      <div className="container mx-auto px-4 py-5 md:px-6 md:py-8">
        <div className="space-y-6">
          <div>
            <Link
              to="/revisr/resource-library"
              className="flex items-center text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Revisr Library
            </Link>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold flex items-center">
                  <Sparkles className="h-8 w-8 mr-2 text-primary" />
                  AI Prompt Templates
                </h1>
                <p className="text-muted-foreground mt-1">
                  Create and manage reusable AI prompts for your documents and
                  projects. A professional default prompt is included to get you
                  started.
                </p>
              </div>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => {
                      setEditingPrompt(null);
                      setFormData({
                        name: "",
                        description: "",
                        content: "",
                      });
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Prompt
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl w-[90vw] max-h-[90vh] flex flex-col">
                  <DialogHeader className="flex-shrink-0 pb-4 border-b">
                    <DialogTitle className="flex items-center gap-3 text-xl font-bold">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Sparkles className="h-5 w-5 text-primary" />
                      </div>
                      {editingPrompt
                        ? "Edit Prompt Template"
                        : "Create New Prompt Template"}
                    </DialogTitle>
                    <DialogDescription className="text-sm mt-2 text-muted-foreground">
                      {editingPrompt
                        ? "Update your prompt template with enhanced AI instructions and variables."
                        : "Create a powerful, reusable AI prompt template for your documents and projects. Use variables to make your prompts dynamic and adaptable."}
                    </DialogDescription>
                  </DialogHeader>
                  <ScrollArea className="flex-1 min-h-0 max-h-[60vh] overflow-y-auto pr-2">
                    <div className="space-y-6 py-4 pr-2">
                      {/* Basic Information Section */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="h-2 w-2 bg-primary rounded-full"></div>
                          <h3 className="text-lg font-semibold text-foreground">
                            Basic Information
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label
                              htmlFor="name"
                              className="text-sm font-medium text-foreground"
                            >
                              Prompt Name *
                            </Label>
                            <Input
                              id="name"
                              value={formData.name}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  name: e.target.value,
                                })
                              }
                              placeholder="e.g., Professional Proposal Generator"
                              className="h-10 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label
                              htmlFor="description"
                              className="text-sm font-medium text-foreground"
                            >
                              Description
                            </Label>
                            <Input
                              id="description"
                              value={formData.description}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  description: e.target.value,
                                })
                              }
                              placeholder="Brief description of what this prompt does"
                              className="h-10 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="h-2 w-2 bg-primary rounded-full"></div>
                          <h3 className="text-lg font-semibold text-foreground">
                            Prompt Content
                          </h3>
                        </div>
                        <div className="space-y-3">
                          <Label
                            htmlFor="content"
                            className="text-sm font-medium text-foreground"
                          >
                            AI Prompt Instructions *
                          </Label>
                          <div className="relative">
                            <Textarea
                              id="content"
                              value={formData.content}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  content: e.target.value,
                                })
                              }
                              placeholder="Enter your detailed AI prompt instructions here. Use {{variableName}} for dynamic variables like {{client.name}}, {{project.title}}, etc."
                              className="min-h-[200px] font-mono text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none leading-relaxed p-4"
                            />
                            <div className="absolute bottom-3 right-3 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded">
                              {formData.content.length} characters
                            </div>
                          </div>
                          <div className="bg-muted/50 rounded-lg p-4 border">
                            <p className="text-sm text-muted-foreground">
                              <strong>Tip:</strong> Use variables like{" "}
                              <code className="bg-background px-1 py-0.5 rounded text-xs">{`{{client.name}}`}</code>
                              ,{" "}
                              <code className="bg-background px-1 py-0.5 rounded text-xs">{`{{project.title}}`}</code>{" "}
                              to make your prompts dynamic and reusable.
                            </p>
                          </div>
                        </div>
                      </div>

                    </div>
                  </ScrollArea>
                  <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full gap-4">
                      <div className="text-sm text-muted-foreground">
                        {editingPrompt
                          ? "Changes will update your custom prompt"
                          : "This will create a new custom prompt template"}
                      </div>
                      <div className="flex gap-3 w-full sm:w-auto">
                        <Button
                          variant="outline"
                          onClick={() => setIsDialogOpen(false)}
                          className="flex-1 sm:flex-none"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSavePrompt}
                          className="gap-2 flex-1 sm:flex-none"
                        >
                          <Sparkles className="h-4 w-4" />
                          {editingPrompt ? "Update Prompt" : "Create Prompt"}
                        </Button>
                      </div>
                    </div>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* View Prompt Dialog */}
              <Dialog
                open={isViewDialogOpen}
                onOpenChange={setIsViewDialogOpen}
              >
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Eye className="h-5 w-5" />
                      View Prompt Template
                    </DialogTitle>
                    <DialogDescription>
                      {viewingPrompt?.description || "No description provided"}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        {viewingPrompt?.name}
                      </h3>
                      {viewingPrompt?.user_id === "system" && (
                        <Badge
                          variant="default"
                          className="mb-3 bg-blue-100 text-blue-800 border-blue-200"
                        >
                          <Star className="h-3 w-3 mr-1" />
                          Default Template
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Prompt Content</Label>
                      <div className="bg-muted/50 rounded-md p-4 border min-h-[200px] max-h-[400px] overflow-y-auto">
                        <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed">
                          {viewingPrompt?.content}
                        </pre>
                      </div>
                    </div>
                    {viewingPrompt?.variables &&
                      viewingPrompt.variables.length > 0 && (
                        <div className="space-y-2">
                          <Label>Variables Used</Label>
                          <div className="flex flex-wrap gap-2">
                            {viewingPrompt.variables.map((variable, index) => (
                              <Badge
                                key={index}
                                variant="outline"
                                className="font-mono"
                              >
                                {`{{${variable}}}`}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setIsViewDialogOpen(false)}
                    >
                      Close
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search prompt templates..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Prompts Grid */}
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredPrompts.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <Sparkles className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  No prompt templates found
                </h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery
                    ? "No prompts match your search."
                    : "Create your first AI prompt template."}
                </p>
                <Button onClick={() => setIsDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Prompt
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPrompts.map((prompt) => (
                <Card
                  key={prompt.id}
                  className="flex flex-col h-[400px] hover:shadow-md transition-shadow"
                >
                  <CardHeader className="pb-3 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {prompt.user_id === "system" && (
                          <Badge
                            variant="default"
                            className="flex items-center gap-1 bg-blue-100 text-blue-800 border-blue-200"
                          >
                            <Star className="h-3 w-3" />
                            Default
                          </Badge>
                        )}
                        {prompt.variables && prompt.variables.length > 0 && (
                          <Badge
                            variant="outline"
                            className="flex items-center gap-1"
                          >
                            <Tag className="h-3 w-3" />
                            {prompt.variables.length} Variables
                          </Badge>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem
                            onClick={() => handleCopyPrompt(prompt)}
                          >
                            {copiedId === prompt.id ? (
                              <>
                                <Check className="mr-2 h-4 w-4" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="mr-2 h-4 w-4" />
                                Copy
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleViewPrompt(prompt)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </DropdownMenuItem>
                          {prompt.user_id === "system" && (
                            <DropdownMenuItem
                              onClick={() => handleDuplicatePrompt(prompt)}
                            >
                              <Files className="mr-2 h-4 w-4" />
                              Duplicate
                            </DropdownMenuItem>
                          )}
                          {prompt.user_id !== "system" && (
                            <>
                              <DropdownMenuItem
                                onClick={() => handleEditPrompt(prompt)}
                              >
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeletePrompt(prompt.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <CardTitle
                      className={`text-lg line-clamp-1 font-semibold ${
                        prompt.user_id === "system" ? "text-blue-700" : ""
                      }`}
                    >
                      {prompt.name}
                    </CardTitle>
                    <CardDescription className="line-clamp-2 text-sm">
                      {prompt.description || "No description provided"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow pt-0 flex flex-col overflow-hidden">
                    <div className="bg-muted/50 rounded-md p-3 mb-3 flex-grow min-h-0">
                      <p className="text-sm text-muted-foreground line-clamp-6 overflow-hidden">
                        {prompt.content.length > 200
                          ? `${prompt.content.substring(0, 200)}...`
                          : prompt.content}
                      </p>
                    </div>
                    <div className="flex-shrink-0 space-y-3">
                      {prompt.variables && prompt.variables.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {prompt.variables
                            .slice(0, 2)
                            .map((variable, index) => (
                              <Badge
                                key={index}
                                variant="secondary"
                                className="flex items-center gap-1 text-xs"
                              >
                                <Tag className="h-3 w-3" />
                                {variable.length > 12
                                  ? `${variable.substring(0, 12)}...`
                                  : variable}
                              </Badge>
                            ))}
                          {prompt.variables.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{prompt.variables.length - 2} more
                            </Badge>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                        <span>
                          Created{" "}
                          {new Date(prompt.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </ScopingAILayout>
  );
}
